import type { RacePhase, SeatReservation } from '@runkr/schema'
import type { ComponentType } from 'preact'
import type { Account } from '../auth/account'
import { makeLazyAuthNotice, makeLazySignIn } from '../auth/lazyAuthUi'
import type { SignInButtonsProps } from '../auth/SignInButtons'
import type { AuthSession } from '../auth/supabase'
import { DailyStore } from '../daily/dailyStore'
import type { NetRace, NetRaceMode, PlayOptions } from '../game/play'
import { lookupCode } from '../net/codeLookup'
import { GameClient } from '../net/GameClient'
import type { NetState } from '../net/GameClient'
import type { ResultRow, RoomSnapshot } from '../net/roomSnapshot'
import { Store } from '../net/store'
import { Party } from '../party/party'
import { sdkPartyConnector } from '../party/partyNet'
import { shareLink } from '../party/shareLink'
import type { PlatformAdapter } from '../platform/PlatformAdapter'
import { makeLazyNameDialog } from '../profile/lazyNameDialog'
import type { ProfileView, WalletView } from '../profile/profileView'
import { Progress } from '../profile/progress'
import { EMOTE_SHOW_MS, emoteForKey } from './emotes'
import { canEmote, initialFlow, isMenuPage, isRoomless, nextFlow } from './flow'
import type { Flow, FlowEvent, JoinKind } from './flow'
import { chunkFailed } from './reload/pageReload'

export interface SessionOptions {
  serverUrl: string
  /** Guest name, known before sign-in finishes. */
  name: string
  auth: Promise<AuthSession>
  platform: PlatformAdapter
  /** `#game`: the Pixi canvas host. */
  gameHost: HTMLElement
  race: PlayOptions
  /** Dev aid (`?lag=<ms>`). */
  lagMs: number
  /** The runner picked in the hub, sent with every join as `options.charId` (T3.1). */
  charId?: () => number
  /** The ride equipped in the hub, sent with every join as `options.ride` (T3.11). */
  ride?: () => string
}

export interface Seat {
  index: number
  name: string
  /** The runner on this seat (`player_N`, D-033), for the matchmaking pedestal (T3.2). */
  charId: number
  isMe: boolean
  isHost: boolean
  connected: boolean
}

/** What the lobby and results screens need from the room, changed only when they change. */
export interface RoomInfo {
  phase: RacePhase
  /** Private room code, '' for public rooms. */
  code: string
  isHost: boolean
  /** By player index; humans only while waiting. */
  seats: Seat[]
  results: ResultRow[]
  mySessionId: string
  /** The race's track (the podium keeps your best time per track, T3.7). */
  trackId: string
}

export type AuthStatus = 'pending' | 'online' | 'offline'

/** The screens' view of a room state (exported for tests). */
export function roomInfo(snap: RoomSnapshot, me: string): RoomInfo {
  return {
    phase: snap.phase,
    code: snap.code,
    isHost: snap.hostSessionId !== '' && snap.hostSessionId === me,
    seats: snap.roster
      .filter((r) => r)
      .map((r) => ({
        index: r.index,
        name: r.displayName,
        charId: r.charId,
        isMe: r.sessionId === me,
        isHost: r.sessionId === snap.hostSessionId,
        connected: r.connected,
      })),
    results: [...snap.results].sort((a, b) => a.placement - b.placement),
    mySessionId: me,
    trackId: snap.trackId,
  }
}

/**
 * One player's online session (PHASE1 T1.8): owns the `GameClient`, the screen flow, the room
 * data the screens show, emotes (keys 1–4) and the platform gameplay hooks. The race scene
 * (`game/play.ts`, with Pixi) is loaded in the background so the menu's bundle stays small.
 */
export class Session {
  readonly net: GameClient
  readonly flow = new Store<Flow>(initialFlow)
  readonly room = new Store<RoomInfo | null>(null)
  readonly auth = new Store<AuthStatus>('pending')
  /** Emote id shown next to each player index, null for none. */
  readonly emotes = new Store<(number | null)[]>([])
  /** The race scene is built (chunk loaded, stage made): matchmaking shows "Loading…" until then. */
  readonly raceReady = new Store<boolean>(false)
  /** Profile, rewards and their components (T2.4). */
  readonly progress: Progress
  /** D-053 slots read by the hub and the results: own profile, wallet, last race's rewards. */
  readonly profile: Store<ProfileView | null>
  readonly wallet: Store<WalletView | null>
  readonly rewardLines: ComponentType
  /** D-053 slot `daily` (T2.5): today's tasks, carrying the `DailyBox` component. */
  readonly daily: DailyStore
  /** The Supabase account once sign-in settles (T2.6); null offline. */
  readonly account = new Store<Account | null>(null)
  /** D-053 slot `signIn` (T2.6): Save progress / Sign in / Signed in with … / Sign out. */
  readonly signIn: ComponentType<SignInButtonsProps>
  /** The account's notice and switch dialog over the menu and the profile (T2.6). */
  readonly AuthNotice: ComponentType
  /** The name dialog is open (`rename()`, T2.6). */
  readonly renaming = new Store<boolean>(false)
  /** The name dialog, mounted once by `App`; shows while `renaming` is true. */
  readonly NameDialog: ComponentType
  /** D-053 slot `party` (T2.7): `{ state, Panel }`, plus the `Bar` that `App` mounts. */
  readonly party: Party
  /** The last code tried, kept in the menu's box after a refusal. */
  lastCode = ''
  private race: NetRace | null = null
  private latest: RoomSnapshot | null = null
  private roomKey = ''
  /** Bumped by every join and leave, so a stale async step does nothing. */
  private seq = 0
  /** Our own `leave()` calls running: their `left` status is not a room event. */
  private quiet = 0
  private gameplay = false
  private readonly emoteTimers = new Map<number, ReturnType<typeof setTimeout>>()

  constructor(private readonly opts: SessionOptions) {
    this.net = new GameClient({
      serverUrl: opts.serverUrl,
      getToken: () => opts.auth.then((a) => a.getToken()),
      name: opts.name,
      lagMs: opts.lagMs,
      ...(opts.charId ? { charId: opts.charId } : {}),
      ...(opts.ride ? { ride: opts.ride } : {}),
    })
    void opts.auth.then((a) => this.auth.set(a.mode))
    this.net.state.subscribe((s) => this.onNetState(s))
    this.net.onSnapshot((snap) => this.onSnapshot(snap))
    this.net.onEmote((m) => this.showEmote(m.player, m.id))
    window.addEventListener('keydown', (e) => this.onKey(e))
    this.progress = new Progress({
      api: opts.auth.then((a) => a.profile),
      onReward: (l) => this.net.onReward(l),
      flow: this.flow,
      room: this.room,
    })
    this.profile = this.progress.profile.view
    this.wallet = this.progress.profile.wallet
    this.rewardLines = this.progress.RewardLines
    this.daily = new DailyStore({
      api: opts.auth.then((a) => a.daily),
      onReward: (l) => this.net.onReward(l),
      flow: this.flow,
    })
    this.signIn = makeLazySignIn(this.account, this.profile)
    this.AuthNotice = makeLazyAuthNotice(this.account)
    this.NameDialog = makeLazyNameDialog(this.renaming, this.progress.profile)
    // A party joins and reserves our seats with the same options as a solo race join (T2.7).
    const raceOptions = () => this.net.joinOptions()
    this.party = new Party({
      connector: sdkPartyConnector(opts.serverUrl, raceOptions),
      origin: window.location.origin,
      onSeat: (seat) => this.takeSeat(seat),
      options: raceOptions,
    })
    // Back on the hub or the results: the runner or ride may have changed since the last seat.
    this.flow.subscribe((f) => {
      if (f.screen === 'menu' || f.screen === 'results') this.party.refreshOptions()
    })
    void opts.auth.then((a) => {
      const account = a.account
      this.account.set(account)
      // A new user without a reload (sign-out, T2.6): read their profile and tasks.
      account?.user.subscribe(() => {
        void this.progress.profile.refresh()
        void this.daily.refresh()
      })
    })
    import('../game/play').then(({ startNetRace }) => {
      // The race intro's banner reads Phase 2's daily slot (D-053, T3.3).
      const daily = () => this.daily
      this.race = startNetRace(opts.gameHost, this.net, this.latest, { ...opts.race, daily }, () =>
        this.raceReady.set(true),
      )
      this.race.setMode(this.raceMode())
    }, chunkFailed)
  }

  get name(): string {
    return this.opts.name
  }

  /** Race / Race again. In a party (T2.7) the leader's call queues the party; a member's waits. */
  playPublic(): void {
    if (this.party.play()) return
    void this.join('public', () => this.net.joinPublic())
  }

  createPrivate(): void {
    void this.join('private', () => this.net.createPrivate())
  }

  joinCode(code: string): void {
    this.lastCode = code
    void this.openCode(code)
  }

  /** A share link `/r/<CODE>` on load (T2.7); null = a malformed code, shown as unknown. */
  openShareLink(code: string | null): void {
    if (code === null) this.dispatch({ type: 'left', reason: 'not-found' })
    else void this.openCode(code)
  }

  /** `<origin>/r/<CODE>` (T2.7), e.g. for a private lobby's Copy link. */
  shareLink(code: string): string {
    return shareLink(window.location.origin, code)
  }

  /** A code from the Join box or a share link: join that party or private race (D-040). */
  private async openCode(code: string): Promise<void> {
    if (isMenuPage(this.flow.get().screen)) return
    const seq = ++this.seq
    const found = await lookupCode(this.opts.serverUrl, code).catch(() => undefined)
    if (seq !== this.seq) return
    if (found === undefined) return this.dispatch({ type: 'left', reason: 'network' })
    if (found === null) return this.dispatch({ type: 'left', reason: 'not-found' })
    if (found.kind === 'race') {
      return this.join('code', () => this.net.joinCode(code, found.roomId))
    }
    const refused = await this.party.join(found.roomId, code)
    if (refused) this.dispatch({ type: 'left', reason: refused })
  }

  /** The party queued (T2.7): take its seat from the hub, a menu page or the results. */
  private takeSeat(seat: SeatReservation): void {
    const { screen } = this.flow.get()
    if (screen === 'race') return // mid-race: this member stays in its race
    if (isMenuPage(screen)) this.dispatch({ type: 'menu' })
    void this.join('public', () => this.net.joinSeat(seat))
  }

  /** Private host in the lobby. */
  start(): void {
    this.net.start()
  }

  /** Private host on the results. */
  rematch(): void {
    this.net.rematch()
  }

  /** The menu's Profile button (D-053 slot): the profile screen, back to the menu from there. */
  openProfile(): void {
    this.dispatch({ type: 'profile' })
  }

  /**
   * Profile → Change name (T2.6, D-039): open the name picker dialog. Does nothing while there is
   * no profile (offline, or before it loaded): names are saved in the profile.
   */
  rename(): void {
    if (this.profile.get()) this.renaming.set(true)
  }

  /** The hub's Settings button (PHASE3 T3.7): the settings screen, back to the menu from there. */
  openSettings(): void {
    this.dispatch({ type: 'settings' })
  }

  /** The hub's Shop button (PHASE3 T3.9a): the shop, back to the menu from there. */
  openShop(): void {
    this.dispatch({ type: 'shop' })
  }

  /** How long the server keeps a room after its results (the rematch countdown), once known. */
  get resultsHoldMs(): number | null {
    return this.race?.resultsHoldMs ?? null
  }

  /** Leave the room (lobby, race, results) and show the menu. */
  toMenu(): void {
    this.seq++
    this.dispatch({ type: 'menu' })
    void this.leaveQuietly()
  }

  private async join(kind: JoinKind, start: () => void): Promise<void> {
    if (isMenuPage(this.flow.get().screen)) return
    const seq = ++this.seq
    this.dispatch({ type: 'join', kind })
    await this.leaveQuietly()
    if (seq !== this.seq) return
    start()
  }

  private async leaveQuietly(): Promise<void> {
    const { status } = this.net.state.get()
    if (status === 'idle' || status === 'left') return
    this.quiet++
    try {
      await this.net.leave()
    } finally {
      this.quiet--
    }
  }

  private onNetState(s: NetState): void {
    if (this.quiet > 0) return
    if (s.status === 'left') this.dispatch({ type: 'left', reason: s.reason })
    else if (s.status === 'offline') this.dispatch({ type: 'offline' })
    // A refusal shown on the menu also stops the retry loop.
    const flow = this.flow.get()
    if (isRoomless(flow) && s.status !== 'left' && s.status !== 'idle') {
      this.seq++
      void this.leaveQuietly()
    }
  }

  private onSnapshot(snap: RoomSnapshot): void {
    const { status, sessionId } = this.net.state.get()
    if (status !== 'joined' && status !== 'reconnecting') return
    this.latest = snap
    const info = roomInfo(snap, sessionId ?? '')
    const key = JSON.stringify(info)
    if (key !== this.roomKey) {
      this.roomKey = key
      this.room.set(info)
    }
    this.dispatch({ type: 'phase', phase: snap.phase })
    if (snap.phase === 'racing' && !this.gameplay) this.setGameplay(true)
    else if (snap.phase !== 'racing' && snap.phase !== 'countdown') this.setGameplay(false)
  }

  private dispatch(ev: FlowEvent): void {
    const next = nextFlow(this.flow.get(), ev)
    if (next === this.flow.get()) return
    this.flow.set(next)
    if (isRoomless(next)) {
      this.setGameplay(false)
      this.room.set(null)
      this.roomKey = ''
      this.latest = null
    }
    this.race?.setMode(this.raceMode())
  }

  private raceMode(): NetRaceMode {
    const { screen } = this.flow.get()
    return screen === 'race' ? 'race' : screen === 'results' ? 'results' : 'hidden'
  }

  private setGameplay(on: boolean): void {
    if (on === this.gameplay) return
    this.gameplay = on
    if (on) this.opts.platform.gameplayStart()
    else this.opts.platform.gameplayStop()
  }

  /** Send emote `id` (keys 1–4, the lobby's emote buttons). */
  emote(id: number): void {
    if (canEmote(this.flow.get())) this.net.emote(id)
  }

  private onKey(e: KeyboardEvent): void {
    const id = emoteForKey(e.code)
    if (id === null || e.repeat || e.ctrlKey || e.altKey || e.metaKey) return
    if (e.target instanceof Element && e.target.closest('input, textarea')) return
    this.emote(id)
  }

  private showEmote(player: number, id: number): void {
    const list = [...this.emotes.get()]
    list[player] = id
    this.emotes.set(list)
    clearTimeout(this.emoteTimers.get(player))
    this.emoteTimers.set(
      player,
      setTimeout(() => {
        const after = [...this.emotes.get()]
        after[player] = null
        this.emotes.set(after)
      }, EMOTE_SHOW_MS),
    )
  }
}
